import * as Sentry from '@sentry/react';
import { AUTH_REGION_URL } from '@/constants/env.constants';

const { logger } = Sentry;

/**
 * Cognito multi-Region replication shares one user pool ID across both Regions, so
 * nothing in the pool ID or the SSO config says which Region is currently serving
 * authentication. The `cognito-check-custom-auth` lambda publishes that verdict as a
 * small document, derived from the same Route 53 composite health check that Cognito
 * uses to route the managed-login custom domain.
 *
 * Shape published by the lambda:
 *
 * ```json
 * {
 *   "schema_version": 1,
 *   "active_region": "us-east-2",
 *   "primary_region": "us-east-2",
 *   "secondary_region": "us-west-2",
 *   "user_pool_endpoint": "https://cognito-idp.us-east-2.amazonaws.com",
 *   "read_only": false,
 *   "health_check_id": "a1b2c3d4-...",
 *   "updated_at": "2026-08-20T09:41:07Z"
 * }
 * ```
 */
export type ActiveAuthRegionSource = 'document' | 'cache';

export type ActiveAuthRegion = {
  region: string;
  /** Pre-computed by the publisher — clients never build AWS hostnames themselves. */
  userPoolEndpoint: string;
  /**
   * True while a secondary (replica) pool is serving. Replicas accept sign-in and
   * token refresh but reject password resets, TOTP enrolment, MFA preference writes
   * and profile updates, so the UI has to gate those.
   */
  isReadOnly: boolean;
  /**
   * The publisher's `updated_at` as epoch milliseconds, or 0 when it is absent or
   * unparseable. Used only to order two versions of the document against each other,
   * never to judge absolute age — see `resolveActiveAuthRegion`.
   */
  publishedAt: number;
  source: ActiveAuthRegionSource;
};

export const AUTH_REGION_CACHE_KEY = 'authActiveRegion';

/**
 * Long enough for a slow edge, short enough to never hold up a cold start.
 *
 * Deliberately a literal rather than an import from @/constants/date.constants: this
 * module is reachable from the axios interceptor, so it ends up in almost every
 * test's import graph, and a partial mock of a shared constants module elsewhere must
 * not be able to break authentication.
 */
export const AUTH_REGION_FETCH_TIMEOUT_MS = 2_000;

type UnknownRecord = Record<string, unknown>;

const isFilledString = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

const isHttpsUrl = (value: string) => {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

const toActiveAuthRegion = (payload: unknown): ActiveAuthRegion | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const document = payload as UnknownRecord;

  // Forward compatible on purpose: require version 1 or later and validate the
  // fields we actually read. A future schema bump must not strand deployed clients
  // on the bundled default, which is exactly the wrong answer during a failover.
  if (typeof document.schema_version !== 'number' || document.schema_version < 1) {
    return null;
  }

  const { active_region: activeRegion, user_pool_endpoint: endpoint, updated_at: updatedAt } = document;

  if (!isFilledString(activeRegion) || !isFilledString(endpoint) || !isHttpsUrl(endpoint)) {
    return null;
  }

  const primaryRegion = document.primary_region;

  return {
    region: activeRegion,
    userPoolEndpoint: endpoint,
    isReadOnly: document.read_only === true || (isFilledString(primaryRegion) && primaryRegion !== activeRegion),
    // A missing or malformed stamp only costs this copy a version comparison. It is
    // not grounds for rejecting an otherwise valid document: the fallback would be
    // the Region compiled into the bundle, which is the worst available answer.
    publishedAt: isFilledString(updatedAt) ? Date.parse(updatedAt) || 0 : 0,
    source: 'document',
  };
};

export const readCachedAuthRegion = (): ActiveAuthRegion | null => {
  try {
    const raw = globalThis.localStorage?.getItem(AUTH_REGION_CACHE_KEY);

    if (!raw) {
      return null;
    }

    const cached = JSON.parse(raw) as UnknownRecord;

    if (!isFilledString(cached.region) || !isFilledString(cached.userPoolEndpoint)) {
      return null;
    }

    return {
      region: cached.region,
      userPoolEndpoint: cached.userPoolEndpoint,
      isReadOnly: cached.isReadOnly === true,
      // Entries written before the stamp was cached lose every comparison, so the
      // next fetched document supersedes them.
      publishedAt: typeof cached.publishedAt === 'number' ? cached.publishedAt : 0,
      source: 'cache',
    };
  } catch {
    return null;
  }
};

const cacheAuthRegion = ({ region, userPoolEndpoint, isReadOnly, publishedAt }: ActiveAuthRegion) => {
  try {
    globalThis.localStorage?.setItem(
      AUTH_REGION_CACHE_KEY,
      JSON.stringify({ region, userPoolEndpoint, isReadOnly, publishedAt }),
    );
  } catch {
    // Storage can be unavailable (private mode, quota). The document is re-read on
    // the next load anyway, so losing the cache costs nothing.
  }
};

export const fetchActiveAuthRegion = async ({ force = false } = {}): Promise<ActiveAuthRegion | null> => {
  // Unset URL means the failover mechanism is not deployed for this environment.
  // Everything then behaves exactly as it did before: the endpoint stays whatever
  // Amplify derives from the user pool ID.
  if (!AUTH_REGION_URL) {
    return null;
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), AUTH_REGION_FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(AUTH_REGION_URL, {
      signal: controller.signal,
      credentials: 'omit',
      // The document is served with a 15s max-age, so the default cache mode keeps a
      // busy tab off the network. The retry path passes force and needs the truth.
      cache: force ? 'reload' : 'default',
    });

    if (!response.ok) {
      return null;
    }

    const resolved = toActiveAuthRegion(await response.json());

    if (!resolved) {
      logger.warn('Active auth region document malformed, falling back', { url: AUTH_REGION_URL });
    }

    return resolved;
  } catch {
    // Offline, aborted or blocked. Silent by design — the caller falls back to the
    // cached value and this runs on every tab focus.
    return null;
  } finally {
    clearTimeout(timeout);
  }
};

/**
 * Returns the newest verdict available, preferring the served document and falling
 * back to our own last-known-good value. Never throws and never blocks longer than
 * the fetch timeout.
 *
 * The document is served from a replicated bucket behind a CloudFront origin group,
 * so a fetch can legitimately return a copy older than one we have already seen. Both
 * stamps are written by the publisher, so ordering them needs no clock of our own —
 * which matters, because the browser's clock is not trustworthy and the response
 * `Date` header is not readable unless the distribution exposes it.
 */
export const resolveActiveAuthRegion = async (options?: { force?: boolean }): Promise<ActiveAuthRegion | null> => {
  const fetched = await fetchActiveAuthRegion(options);
  const cached = readCachedAuthRegion();

  if (!fetched) {
    return cached;
  }

  if (cached && cached.publishedAt > fetched.publishedAt) {
    logger.warn('Served auth region document is older than the cached one, keeping the cache', {
      url: AUTH_REGION_URL,
      servedRegion: fetched.region,
      cachedRegion: cached.region,
    });

    return cached;
  }

  cacheAuthRegion(fetched);

  return fetched;
};
